// Dialogue System for Club Penguin Game
// Displays one message (or a sequence) in the dialogue box. Click the box
// or press Space/Enter to advance. Starting a new dialogue cancels the
// current one without firing its completion callback.

import type { DialogueMessage } from '@/config/room-types';

interface DialogueOptions {
  onComplete?: () => void;
  x?: number;
  y?: number;
}

export class DialogueSystem {
  private box: HTMLElement;
  private textEl: HTMLElement;

  private messages: DialogueMessage[] = [];
  private index = 0;
  private onComplete: (() => void) | null = null;

  private clickHandler: () => void;
  private keydownHandler: (e: KeyboardEvent) => void;

  constructor() {
    this.box = document.getElementById('dialogue-box') ?? this.createBox();
    this.textEl = this.box.querySelector<HTMLElement>('#dialogue-text') ?? this.createTextEl();

    this.clickHandler = () => this.advance();
    this.keydownHandler = e => {
      if (this.isActive() && (e.code === 'Space' || e.code === 'Enter')) {
        e.preventDefault();
        this.advance();
      }
    };

    this.box.addEventListener('click', this.clickHandler);
    document.addEventListener('keydown', this.keydownHandler);
  }

  public show(messages: DialogueMessage | DialogueMessage[], options: DialogueOptions = {}): void {
    const list = Array.isArray(messages) ? messages : [messages];
    if (list.length === 0) return;

    this.messages = list;
    this.index = 0;
    this.onComplete = options.onComplete ?? null;

    this.position(options.x, options.y);
    this.box.classList.add('active');
    this.render();
  }

  public isActive(): boolean {
    return this.box.classList.contains('active');
  }

  /** Close the dialogue and fire its completion callback. */
  public hide(): void {
    if (!this.isActive()) return;
    const callback = this.onComplete;
    this.clear();
    callback?.();
  }

  /** Close the dialogue without firing its completion callback. */
  public cancel(): void {
    this.clear();
  }

  public destroy(): void {
    this.clear();
    this.box.removeEventListener('click', this.clickHandler);
    document.removeEventListener('keydown', this.keydownHandler);
  }

  // Convenience wrappers
  public showMessage(speaker: string, text: string, onComplete?: () => void): void {
    this.show({ speaker, text }, { onComplete });
  }

  public showMessageAt(
    speaker: string,
    text: string,
    x: number,
    y: number,
    onComplete?: () => void,
  ): void {
    this.show({ speaker, text }, { x, y, onComplete });
  }

  public showConversation(messages: DialogueMessage[], onComplete?: () => void): void {
    this.show(messages, { onComplete });
  }

  private advance(): void {
    if (!this.isActive()) return;
    this.index++;
    if (this.index >= this.messages.length) {
      this.hide();
    } else {
      this.render();
    }
  }

  private render(): void {
    this.textEl.textContent = this.messages[this.index].text;
  }

  private clear(): void {
    this.box.classList.remove('active');
    this.messages = [];
    this.index = 0;
    this.onComplete = null;
  }

  private position(x?: number, y?: number): void {
    if (x !== undefined) {
      this.box.style.left = `${x}px`;
      this.box.style.transform = 'none';
    } else {
      this.box.style.left = '50%';
      this.box.style.transform = 'translateX(-50%)';
    }

    if (y !== undefined) {
      this.box.style.top = `${y}px`;
      this.box.style.bottom = 'auto';
    } else {
      this.box.style.bottom = '40px';
      this.box.style.top = 'auto';
    }
  }

  private createBox(): HTMLElement {
    const box = document.createElement('div');
    box.id = 'dialogue-box';
    (document.getElementById('game-container') ?? document.body).appendChild(box);
    return box;
  }

  private createTextEl(): HTMLElement {
    const textEl = document.createElement('div');
    textEl.id = 'dialogue-text';
    this.box.appendChild(textEl);
    return textEl;
  }
}
